import NewIcon from 'assets/Header/NewIcon';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import React, { FC, useEffect, useRef, useState } from 'react';
import { BiGlobe, BiPhone, BiSearch, BiX } from 'react-icons/bi';
import { twMerge } from 'tailwind-merge';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';
import { Course } from '@/components/Courses/CoursesCard';
import Divider from '@/components/Layout/Header/Search/Divider';
import Suggestions from '@/components/Layout/Header/Search/Suggestions';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { Book } from '@/utils/interfaces/Library/Books';

import { ISchool } from '@/types/schools';

interface Props {
  isSecondRowHidden: boolean;
  className?: string;
  classNameContainer?: string;
  classNameContact?: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setShowSearch: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
  isShopPage: boolean;
  validPathSearch: string[];
  isSearch: boolean;
  newIconId: string;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  books?: Book[];
  oranges?: Book[];
  courses: Course[];
  bundles?: Book[];
  schools?: ISchool[];
  showSuggestionsPhone: boolean;
}

export enum OptionsEnum {
  BULK_ORDER = 'Bulk Order',
  PARTNERSHIP = 'Partnerships',
  PUBLIHS_WITH_US = 'Publish with us',
  CONTACT = 'Contact',
}
export enum OptionLinksEnum {
  BULK_ORDER = 'https://business.zebralearn.com/business',
  PARTNERSHIP = '/partnerships',
  PUBLIHS_WITH_US = 'https://studio.zebralearn.com/',
  CONTACT = '/contact',
}
const Search: FC<Props> = ({
  setShowSearch,
  className,
  search,
  setSearch,
  isSearch,
  newIconId,
  books,
  oranges,
  courses,
  bundles,
  schools,
  showSuggestionsPhone,
  isSecondRowHidden,
}) => {
  const twScreens = useTailwindBreakpoint();
  const [placeholderIndex, setPlaceholderIndex] = React.useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const isMobile = !twScreens.xl;
  const animatedWords = ['"books"', '"courses"', '"schools"', '"live"'];
  useEffect(() => {
    if (!isMobile) return;

    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % animatedWords.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [isMobile]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setShowSuggestions(false);
  }, [isSecondRowHidden]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className={twMerge(
          'relative gap-[10px] z-50 border-[1.5px] box-border sm:border-grayZl-900  xl:h-10 sm:h-[32px] h-10 flex items-center justify-between lg:mb-0 sm:mb-2 mb-3 ',

          isSearch ? 'flex-1 lg:flex-1 sm:flex-none' : '',
          showSuggestions || showSuggestionsPhone
            ? 'bg-grayZl-925 rounded-t-[12px] sm:rounded-t-xl border-b-0 sm:rounded-tr-xl rounded-tr-none border-grayZl-900 sm:border-t-[1.5px] sm:border-l-[1.5px] sm:border-r-[1.5px] border-r-0 transition-all'
            : ' sm:rounded-xl rounded-[12px] border-grayZl-850 sm:rounded-r-[12px] rounded-r-none  sm:border-r-[1.5px] border-r-0',
          className,
          'max-w-[1019px]',
        )}
        onClick={() => setShowSuggestions(true)}
        ref={searchRef}
      >
        <RenderIf condition={isSearch}>
          <div className='flex items-center gap-2  sm:ps-2 ps-3 flex-1 sm:pe-0 pe-2 h-full'>
            <BiSearch className='xl:w-6 xl:h-6 xl:min-w-6 xl:min-h-6 sm:w-[18px] w-5 sm:h-[18px] h-5 lg:min-w-[18px] lg:min-h-[18px] sm:text-white text-grayZl-500' />
            <div className='flex relative flex-1'>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='flex-1 font-medium text-white bg-transparent outline-none xs400:w-auto w-[100px]'
                placeholder=''
              />

              {!search && isMobile && (
                <div className='absolute inset-0 flex items-center pointer-events-none flex-nowrap truncate flex-1'>
                  <span className='text-grayZl-700 font-medium text-sm'>
                    Search for{' '}
                  </span>

                  <AnimatePresence mode='wait'>
                    <motion.span
                      key={placeholderIndex}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.3 }}
                      className='text-grayZl-700 font-medium text-sm ml-1'
                    >
                      {animatedWords[placeholderIndex]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )}

              {!isMobile && (
                <div className='absolute inset-0 flex items-center pointer-events-none'>
                  {!search && (
                    <span className='text-grayZl-700 font-medium text-sm'>
                      Search books, courses, schools and live
                    </span>
                  )}
                </div>
              )}
            </div>
            <ShouldRender check={search}>
              <button
                onClick={() => {
                  setSearch('');
                  setShowSearch(false);
                }}
                className='hover:scale-105 transition-all '
              >
                <BiX className='w-5 h-5 text-grayZl-600' />
              </button>
            </ShouldRender>
          </div>
        </RenderIf>

        <div className='h-full sm:flex hidden '>
          <RenderIf condition={isSearch}>
            <Divider />
          </RenderIf>
          <Link
            onClick={(e) => {
              e.stopPropagation();
            }}
            href={OptionLinksEnum.PARTNERSHIP}
            className='flex'
          >
            <button
              className={twMerge(
                'flex items-center xl:gap-1.5 gap-2 xl:px-[13px] px-[12px] hover:bg-grayZl-900 transition-all hover:scale-105 active:scale-95 ',
                !isSearch ? 'sm:rounded-s-xl rounded-s-[15px]' : '',
              )}
            >
              <p className='xl:text-sm/[22px] text-xs/[20px] font-medium text-white text-nowrap'>
                {OptionsEnum.PARTNERSHIP}
              </p>
              <NewIcon
                id={newIconId}
                className='w-[40px] xl:h-[24px] h-[18px]'
              />
            </button>
          </Link>
          <Divider />
          <Link
            onClick={(e) => {
              e.stopPropagation();
            }}
            href={OptionLinksEnum.PUBLIHS_WITH_US}
            className='flex'
          >
            <button className='flex items-center gap-1 xl:px-[13px] px-[12px] hover:bg-grayZl-900 transition-all hover:scale-105 active:scale-95'>
              <BiGlobe className='w-5 h-5 text-grayZl-500' />
              <p className='xl:text-sm/[22px] text-xs/[20px] font-medium text-white text-nowrap'>
                {OptionsEnum.PUBLIHS_WITH_US}
              </p>
            </button>
          </Link>
          <Divider />
          <Link
            onClick={(e) => {
              e.stopPropagation();
            }}
            href={OptionLinksEnum.CONTACT}
            className='flex'
          >
            <button
              className={twMerge(
                'flex items-center gap-1 xl:ps-[13px] ps-[12px] pe-[14px] hover:bg-grayZl-900 transition-all hover:scale-105 active:scale-95 ',
                showSuggestions
                  ? 'sm:rounded-tr-xl sm:rounded-br-none rounded-e-[15px]'
                  : 'sm:rounded-e-xl rounded-e-[15px]',
              )}
            >
              <BiPhone className='w-5 h-5 text-grayZl-500' />
              <p className='xl:text-sm/[22px] text-xs/[20px] font-medium text-white text-nowrap'>
                {OptionsEnum.CONTACT}
              </p>
            </button>
          </Link>
        </div>
        <AnimatePresence>
          <RenderIf condition={twScreens.sm && showSuggestions}>
            {twScreens.sm && showSuggestions && (
              <Suggestions
                schools={schools}
                onClickBook={() => {
                  setShowSuggestions(false);
                }}
                onClickTrending={() => {
                  setShowSuggestions(false);
                }}
                bundles={bundles}
                courses={courses}
                books={books}
                oranges={oranges}
                mode={search ? 'suggestions' : 'tranding'}
              />
            )}
          </RenderIf>
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default Search;
